import { Heading, Text } from '@higo/ui';
import {
  SetNewPasswordForm,
  SetNewPasswordFormModel,
} from './SetNewPasswordForm';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from 'routes';
import { useSetNewPasswordMutation } from './useSetNewPasswordMutation';
import { toast } from 'react-toastify';
import { handleAxiosError } from '@higo/common/lib/ext/ApiError';
import axios from 'axios';

const StyledText = styled(Text)`
  margin-bottom: 1.5rem;
`;

export type SetNewPasswordPageParams = {
  token: string;
};

export const SetNewPasswordPage = () => {
  const { token } = useParams<SetNewPasswordPageParams>();
  const navigate = useNavigate();
  const mutation = useSetNewPasswordMutation();
  const intl = useIntl();
  const [passwordServerError, setPasswordServerError] = useState<string | null>(
    null,
  );

  if (!token) {
    navigate(routes.root);
    return null;
  }

  const requestSetNewPassword = ({
    password,
    passwordConfirmation,
  }: SetNewPasswordFormModel) => {
    setPasswordServerError(null);

    return mutation
      .mutateAsync({
        newPassword: password,
        repeatNewPassword: passwordConfirmation,
        token,
      })
      .then(() => {
        navigate(routes.root);
      })
      .catch((error) => {
        const responseData = axios.isAxiosError(error)
          ? (error.response?.data as
              | { errors?: Array<{ errorCode?: string }> }
              | undefined)
          : undefined;
        const errorCode = responseData?.errors?.[0]?.errorCode;

        if (errorCode === 'UE.WRP') {
          setPasswordServerError(errorCode);
          return;
        }

        const translatedError = handleAxiosError(error as Error, intl);
        toast.error(
          Array.isArray(translatedError.message)
            ? translatedError.message[0]
            : translatedError.message,
        );
      });
  };

  return (
    <>
      <Heading level={1}>
        <FormattedMessage id="authentication.setNewPassword.newPassword" />
      </Heading>
      <StyledText variant="body2">
        <FormattedMessage id="authentication.setNewPassword.description" />
      </StyledText>
      <SetNewPasswordForm
        onSubmit={requestSetNewPassword}
        passwordServerError={passwordServerError}
        onPasswordServerErrorClear={() => setPasswordServerError(null)}
      />
    </>
  );
};
